import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useState } from 'react'
import { adminProductionsService } from '../services/adminProductions.service'
import type { ProductionDocument } from '@/types'

/**
 * Fetches a single production by id. Returns the document data with its id
 * merged in — Firestore's snapshot.data() omits the id otherwise, which has
 * tripped up callers building URLs like `/admin/productions/${production.id}`.
 */
export function useProduction(productionId: string | null | undefined) {
  const [production, setProduction] = useState<(ProductionDocument & { id: string }) | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!productionId) {
      setProduction(null)
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)
    return adminProductionsService.subscribeById(
      productionId,
      (data) => {
        setProduction(data ? { ...data, id: productionId } : null)
        setLoading(false)
        setError(null)
      },
      (err: unknown) => {
        setError(formatUserFacingError(err, 'Failed to load production.'))
        setLoading(false)
      }
    )
  }, [productionId, tick])

  return { production, loading, error, refresh }
}
